// Centralized permission system for role-based access control

export type GlobalRole = 'founder' | 'admin' | 'employee' | 'investor' | 'advisor'
export type ProjectRole = 'owner' | 'admin' | 'member'

interface ProjectMember {
  project_id: string
  user_id: string
  role: string  // Changed from ProjectRole to string to match context
}

export interface UserPermissions {
  // Global permissions (based on user.role)
  canInviteUsers: boolean
  canManageAllProjects: boolean
  canViewAllProjects: boolean
  isViewOnly: boolean

  // Project-specific permissions
  canEditProject: boolean
  canDeleteProject: boolean
  canManageProjectMembers: boolean
  canAssignTasks: boolean
  canEditTasks: boolean
  canViewTasks: boolean
  canCreateContributions: boolean
  canMakeEquityOffers: boolean
}

// Get global permissions based on user role
export function getGlobalPermissions(userRole?: GlobalRole): Partial<UserPermissions> {
  if (!userRole) return {}

  switch (userRole) {
    case 'founder':
    case 'admin':
      return {
        canInviteUsers: true,
        canManageAllProjects: true,
        canViewAllProjects: true,
        isViewOnly: false,
        canMakeEquityOffers: true,
      }
    case 'employee':
      return {
        canInviteUsers: false,
        canManageAllProjects: false,
        canViewAllProjects: false,
        isViewOnly: false,
        canMakeEquityOffers: false,
      }
    case 'investor':
    case 'advisor':
      return {
        canInviteUsers: false,
        canManageAllProjects: false,
        canViewAllProjects: true,
        isViewOnly: true,
        canMakeEquityOffers: false,
      }
    default:
      return {
        canInviteUsers: false,
        canManageAllProjects: false,
        canViewAllProjects: false,
        isViewOnly: false,
        canMakeEquityOffers: false,
      }
  }
}

// Get project-specific permissions based on project role
export function getProjectPermissions(projectRole?: ProjectRole): Partial<UserPermissions> {
  if (!projectRole) return {
    canEditProject: false,
    canDeleteProject: false,
    canManageProjectMembers: false,
    canAssignTasks: false,
    canEditTasks: false,
    canViewTasks: false,
  }

  switch (projectRole) {
    case 'owner':
      return {
        canEditProject: true,
        canDeleteProject: true,
        canManageProjectMembers: true,
        canAssignTasks: true,
        canEditTasks: true,
        canViewTasks: true,
        canCreateContributions: true,
      }
    case 'admin':
      return {
        canEditProject: true,
        canDeleteProject: false, // Only owners can delete
        canManageProjectMembers: true,
        canAssignTasks: true,
        canEditTasks: true,
        canViewTasks: true,
        canCreateContributions: true,
      }
    case 'member':
    default:
      return {
        canEditProject: false,
        canDeleteProject: false,
        canManageProjectMembers: false,
        canAssignTasks: false,
        canEditTasks: true, // Members can edit tasks
        canViewTasks: true,
        canCreateContributions: true,
      }
  }
}

// Combine global and project permissions
export function getUserPermissions(
  user: any,
  projectRole?: ProjectRole
): UserPermissions {
  console.log('=== getUserPermissions Debug ===')
  console.log('Raw user data:', user)
  console.log('Project role:', projectRole)

  // Extract global role from user data - handle different data structures
  let globalRole: GlobalRole | undefined

  if (user?.roles?.name) {
    globalRole = user.roles.name as GlobalRole
    console.log('Found global role from user.roles.name:', globalRole)
  } else if (user?.role) {
    globalRole = user.role as GlobalRole
    console.log('Found global role from user.role:', globalRole)
  } else if (user?.role_id) {
    // Map role_id to role name if needed (this is a fallback)
    // We might need to enhance this later with a proper role lookup
    globalRole = 'employee' // Default fallback
    console.log('Using fallback employee role for role_id:', user.role_id)
  }

  console.log('Initial global role determined:', globalRole)

  // Special case: if user is a project owner but doesn't have a global role set,
  // they should get some elevated permissions
  if (!globalRole && projectRole === 'owner') {
    globalRole = 'founder'
    console.log('Elevated to founder because project owner with no global role')
  }

  console.log('Final global role:', globalRole)

  const globalPerms = getGlobalPermissions(globalRole)
  const projectPerms = getProjectPermissions(projectRole)

  console.log('Global permissions:', globalPerms)
  console.log('Project permissions:', projectPerms)

  // Check if view-only user
  const isViewOnly = globalPerms.isViewOnly ?? false

  // Debug logging
  const canMakeEquityOffers = (globalPerms.canMakeEquityOffers ?? false) || (projectRole === 'owner')
  console.log('Final permissions calculation:', {
    globalRole,
    projectRole,
    isViewOnly,
    globalCanMakeOffers: globalPerms.canMakeEquityOffers,
    finalCanMakeOffers: canMakeEquityOffers,
    user: user ? { id: user.id, role: user.role, roles: user.roles } : null
  })

  const finalPermissions = {
    // Global permissions
    canInviteUsers: globalPerms.canInviteUsers ?? false,
    canManageAllProjects: globalPerms.canManageAllProjects ?? false,
    canViewAllProjects: globalPerms.canViewAllProjects ?? false,
    isViewOnly,
    // Project owners can create equity offers even if they don't have global admin rights
    canMakeEquityOffers,

    // Project permissions (restricted for view-only users)
    canEditProject: isViewOnly ? false : (projectPerms.canEditProject ?? false),
    canDeleteProject: isViewOnly ? false : (projectPerms.canDeleteProject ?? false),
    canManageProjectMembers: isViewOnly ? false : (projectPerms.canManageProjectMembers ?? false),
    canAssignTasks: isViewOnly ? false : (projectPerms.canAssignTasks ?? false),
    canEditTasks: isViewOnly ? false : (projectPerms.canEditTasks ?? false),
    canViewTasks: projectPerms.canViewTasks ?? true, // Default to true for viewing
    canCreateContributions: isViewOnly ? false : (projectPerms.canCreateContributions ?? false),
  }

  console.log('Final calculated permissions:', finalPermissions)
  console.log('=== End getUserPermissions Debug ===')

  return finalPermissions
}

// Helper to get project role for a user
export function getUserProjectRole(
  userId: string | undefined,
  projectId: string | undefined,
  projectMembers: ProjectMember[]
): ProjectRole | undefined {
  if (!userId || !projectId) return undefined

  const member = projectMembers.find(
    m => m.project_id === projectId && m.user_id === userId
  )

  return member?.role as ProjectRole | undefined
}